import { useQuery } from '@tanstack/react-query';
import { Card, Col, Row, Space, Statistic, Tabs, Tag, Typography } from 'antd';
import {
  CreditCardOutlined,
  CheckCircleOutlined,
  KeyOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { get } from '../../api/client';
import type { AdminChannelView, GlobalPaymentConfig } from './types';
import { ChannelsPanel } from './components/ChannelsPanel';
import { GlobalConfigPanel } from './components/GlobalConfigPanel';

const TEAL = '#0d9488';

/**
 * 支付管理页。
 * 结构：顶部状态概览 + 两个标签页（支付渠道 / 全局配置）。
 * - 支付渠道：卡片式 CRUD，密钥经脱敏表单只写不回显（见 ChannelFormModal）。
 * - 全局配置：mock/real 支付模式卡片切换 + 回调地址。
 * 银行转账人工审批独立成页（/payment-transfers），邀请记录并入了会员管理的「邀请记录」栏。
 */
export default function PaymentPage() {
  const { data: channels } = useQuery({
    queryKey: ['payment-channels'],
    queryFn: () => get<AdminChannelView[]>('/admin/payment-channels'),
  });
  const { data: config } = useQuery({
    queryKey: ['payment-config'],
    queryFn: () => get<GlobalPaymentConfig>('/admin/payment-config'),
  });

  const list = channels ?? [];
  const mode = config?.paymentMode ?? 'mock';
  const activeCount = list.filter((c) => c.isActive).length;
  const secretCount = list.filter((c) => c.hasSecrets).length;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          支付管理
        </Typography.Title>
        <Typography.Text type="secondary">
          配置支付宝 / 微信 / 银行转账渠道与全局支付模式。所有密钥加密存储、只写不回显，保障资金与凭据安全。
        </Typography.Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} lg={6}>
          <Card styles={{ body: { padding: 20 } }}>
            <Space align="center" size={16}>
              <IconTile color="#1677ff">
                <CreditCardOutlined />
              </IconTile>
              <Statistic title="支付渠道" value={list.length} styles={{ content: { color: '#1f2329', fontWeight: 600 } }} />
            </Space>
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card styles={{ body: { padding: 20 } }}>
            <Space align="center" size={16}>
              <IconTile color={TEAL}>
                <CheckCircleOutlined />
              </IconTile>
              <Statistic title="已启用" value={activeCount} styles={{ content: { color: '#1f2329', fontWeight: 600 } }} />
            </Space>
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card styles={{ body: { padding: 20 } }}>
            <Space align="center" size={16}>
              <IconTile color="#f59e0b">
                <KeyOutlined />
              </IconTile>
              <Statistic title="已配密钥" value={secretCount} styles={{ content: { color: '#1f2329', fontWeight: 600 } }} />
            </Space>
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card styles={{ body: { padding: 20 } }}>
            <Space align="center" size={16}>
              <IconTile color={mode === 'real' ? TEAL : '#f59e0b'}>
                <ThunderboltOutlined />
              </IconTile>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                  支付模式
                </Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Tag color={mode === 'real' ? 'success' : 'warning'} style={{ fontSize: 13, padding: '2px 10px' }}>
                    {mode === 'real' ? '真实收款' : '模拟支付'}
                  </Tag>
                </div>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card styles={{ body: { paddingTop: 8 } }}>
        <Tabs
          defaultActiveKey="channels"
          items={[
            { key: 'channels', label: '支付渠道', children: <ChannelsPanel /> },
            { key: 'global', label: '全局配置', children: <GlobalConfigPanel /> },
          ]}
        />
      </Card>
    </div>
  );
}

/** 统计卡图标底座 */
function IconTile({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 14,
        background: `${color}1a`,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 22,
      }}
    >
      {children}
    </div>
  );
}
